import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="mention"
// Provides @ mention dropdown for section-level editing in AI chat
export default class extends Controller {
  static targets = ["input", "dropdown", "contextId", "contextType"]
  static values = { sections: Array }

  connect() {
    this.mentionActive = false
    this.mentionStart = -1
    this.filteredSections = []
    this.selectedIndex = 0
    this.contextSetByMention = false
  }

  onInput(event) {
    const input = this.inputTarget
    const value = input.value
    const cursorPos = input.selectionStart

    // Find if we're in an @ mention
    const textBeforeCursor = value.substring(0, cursorPos)
    const atIndex = textBeforeCursor.lastIndexOf("@")

    if (atIndex >= 0 && (atIndex === 0 || textBeforeCursor[atIndex - 1] === " ")) {
      const query = textBeforeCursor.substring(atIndex + 1).toLowerCase()
      this.mentionStart = atIndex
      this.mentionActive = true

      // Filter sections by query
      this.filteredSections = this.sectionsValue.filter(s =>
        s.name.toLowerCase().includes(query)
      )
      this.selectedIndex = 0

      if (this.filteredSections.length > 0) {
        this.showDropdown()
      } else {
        this.hideDropdown()
      }
    } else {
      this.mentionActive = false
      this.hideDropdown()
    }
  }

  onKeydown(event) {
    if (!this.mentionActive || this.filteredSections.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredSections.length - 1)
      this.renderDropdown()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
      this.renderDropdown()
    } else if (event.key === "Enter" && this.mentionActive && this.filteredSections.length > 0) {
      event.preventDefault()
      this.selectSection(this.filteredSections[this.selectedIndex])
    } else if (event.key === "Escape") {
      this.hideDropdown()
      this.mentionActive = false
    }
  }

  selectFromDropdown(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (this.filteredSections[index]) {
      this.selectSection(this.filteredSections[index])
    }
  }

  selectSection(section) {
    const input = this.inputTarget
    const value = input.value
    const beforeMention = value.substring(0, this.mentionStart)
    const afterCursor = value.substring(input.selectionStart)

    input.value = `${beforeMention}@${section.name} ${afterCursor}`
    input.focus()

    // Set hidden context fields for section-level editing
    if (this.hasContextIdTarget) {
      this.contextIdTarget.value = section.id
      this.contextSetByMention = true
    }
    if (this.hasContextTypeTarget) {
      this.contextTypeTarget.value = "ReportSection"
    }
    this.contextSetByMention = true

    this.hideDropdown()
    this.mentionActive = false
  }

  showDropdown() {
    this.renderDropdown()
    this.dropdownTarget.classList.remove("hidden")
  }

  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  renderDropdown() {
    const items = this.filteredSections.map((section, i) => {
      const isSelected = i === this.selectedIndex
      return `<button type="button"
        data-index="${i}"
        data-action="click->mention#selectFromDropdown"
        class="w-full text-left px-3 py-1.5 text-xs ${isSelected ? 'bg-purple-50 text-purple-700' : 'text-gray-700 hover:bg-gray-50'} cursor-pointer flex items-center gap-2">
        <i data-lucide="table-2" class="w-3 h-3 inline text-gray-400"></i>
        <span class="font-medium">${section.name}</span>
        <span class="text-[10px] text-gray-400 ml-auto">${section.template_name || ''}</span>
      </button>`
    }).join("")

    this.dropdownTarget.innerHTML = items
  }

  // Clear section context when user removes the @ mention text
  // Only clear if the context was set by the mention controller (@ section selection),
  // not if it was set by the AI broadcast (component context for follow-up edits)
  clearContext() {
    if (this.contextSetByMention && this.hasContextIdTarget && this.inputTarget.value.indexOf("@") === -1) {
      this.contextIdTarget.value = ""
      this.contextTypeTarget.value = ""
      this.contextSetByMention = false
    }
  }
}
